// BaseAudioManager.ts
import { ref, type Ref } from 'vue';
import {
  FileUploadButton,
  UpstreamState,
  ToAudioServer,
  ClientConnectRequest,
  ToAudioClient,
  AudioTranscript,
  FileChunk,
  ButtonAction,
  MeetingJoinRequest
} from '../proto-gen-ref/audio_pb';
import type { IAudioManager, MessageButton, MeetingRoute, ParticipantUpdate } from './types';
import { ToastPluginApi } from 'vue-toast-notification';
import type { GameStreamingService } from './GameManager';

/**
 * Base abstract class that implements common functionality between real and mock audio managers
 * Uses multiplexed connection through GameManager
 */
export abstract class BaseAudioManager implements IAudioManager {
  protected gameManager: GameStreamingService;
  protected toast: ToastPluginApi;
  protected isMutingRecording: Ref<boolean> = ref(false);
  protected lastKnownUpstreamStatusPrimary: Ref<UpstreamState> = ref(UpstreamState.NO_CONNECTION);
  protected lastKnownUpstreamStatusObserver: Ref<UpstreamState> = ref(UpstreamState.NO_CONNECTION);
  protected speechMessageCallbacks: Array<(source: string, text: string, itemId: string, buttons?: MessageButton[]) => void> = [];
  protected fileUploadButtonCallbacks: Array<(customization: FileUploadButton) => void> = [];
  protected lastStaticFileUploadButton: FileUploadButton | null = null;
  protected audioConnKey: string | null = null;
  protected selectedUserAudioMeetingId: string;
  protected route: MeetingRoute;

  // For participant updates
  protected participantUpdateCallbacks: Array<(update: ParticipantUpdate) => void> = [];
  protected lastParticipantUpdate: ParticipantUpdate | null = null;

  // For file chunks
  protected fileChunkCallbacks: Array<(chunk: FileChunk) => void> = [];

  constructor(
    gameManager: GameStreamingService,
    toast: ToastPluginApi,
    selectedUserAudioMeetingId: string,
    route: MeetingRoute
  ) {
    this.gameManager = gameManager;
    this.toast = toast;
    this.selectedUserAudioMeetingId = selectedUserAudioMeetingId;
    this.route = route;

    // Don't call sendJoinMeeting() here - it will be called after
    // the subclass constructor completes to ensure all fields are initialized
  }

  public async sendJoinMeeting(): Promise<void> {
    const joinReq = new MeetingJoinRequest()
      .setUserAudioMeetingId(this.selectedUserAudioMeetingId)
      .setOpenAiChatModel('')  // Will be set by backend from login
      .setOpenAiRealtimeModel('');  // Will be set by backend from login

    const toServer = new ToAudioServer().setJoinMeeting(joinReq);
    this.gameManager.sendAudioMessage(toServer);
    console.log('[BaseAudioManager] Sent JOIN_MEETING with meetingId:', this.selectedUserAudioMeetingId);

    // Initialize audio hardware after joining the meeting
    await this.onConnected();
    console.log('[BaseAudioManager] Audio hardware initialized successfully');
  }

  // Common implementations
  public isMicMuted(): boolean {
    return this.isMutingRecording.value;
  }

  public getLastKnownUpstreamStatusPrimary(): UpstreamState {
    return this.lastKnownUpstreamStatusPrimary.value;
  }

  public getLastKnownUpstreamStatusObserver(): UpstreamState {
    return this.lastKnownUpstreamStatusObserver.value;
  }

  // Common method for sending audio messages through multiplexed connection
  protected txAudio(serializedBinary: Uint8Array): void {
    // Deserialize to ToAudioServer and send via game manager
    try {
      const toAudioServer = ToAudioServer.deserializeBinary(serializedBinary);
      this.gameManager.sendAudioMessage(toAudioServer);
    } catch (error) {
      console.error('[BaseAudioManager] Error deserializing audio message:', error);
    }
  }

  // Common text submission
  public submitText(text: string): void {
    console.log('[BaseAudioManager] Submitted text:', text);
    if (this.route !== '/chat') return;
    const toServer = new ToAudioServer()
      .setUserSubmittedText(text);
    this.txAudio(toServer.serializeBinary());
  }

  // Common button action
  public sendButtonAction(buttonId: string, action: string): void {
    if (this.route !== '/chat') return;

    const buttonAction = new ButtonAction()
      .setButtonId(buttonId)
      .setAction(action);

    const toServer = new ToAudioServer();
    toServer.setButtonAction(buttonAction);

    console.log(`[BaseAudioManager] Sending ButtonAction: buttonId=${buttonId}, action=${action}`);
    this.txAudio(toServer.serializeBinary());
  }

  // Request Autodesk OAuth URL
  public requestAutodeskOAuthUrl(): void {
    if (this.route !== '/chat') return;
    
    const toServer = new ToAudioServer()
      .setGetAutodeskOauthUrl('request');
    
    console.log('[BaseAudioManager] Requesting Autodesk OAuth URL');
    this.txAudio(toServer.serializeBinary());
  }

  // Common callback registration
  public onSpeechMessage(callback: (source: string, text: string, itemId: string, buttons?: MessageButton[]) => void) {
    this.speechMessageCallbacks.push(callback);
  }

  public onFileUploadButton(callback: (customization: FileUploadButton) => void) {
    this.fileUploadButtonCallbacks.push(callback);
    
    // If we already received a static button message, replay it immediately
    if (this.lastStaticFileUploadButton) {
      console.log('[BaseAudioManager] Replaying last static file upload button for new callback');
      callback(this.lastStaticFileUploadButton);
    }
  }

  public onFileChunk(callback: (chunk: FileChunk) => void): void {
    this.fileChunkCallbacks.push(callback);
    console.log('[BaseAudioManager] Registered file chunk callback');
  }

  public onParticipantUpdate(callback: (update: ParticipantUpdate) => void): ParticipantUpdate | null {
    this.participantUpdateCallbacks.push(callback);
    return this.lastParticipantUpdate;
  }

  public removeParticipantUpdateListeners(): void {
    this.participantUpdateCallbacks = [];
  }

  // Common file handling
  public sendFileChunk(fileChunk: FileChunk): void {
    const toServer = new ToAudioServer();
    toServer.setFileChunk(fileChunk);
    this.txAudio(toServer.serializeBinary());
  }

  // Abstract method that all implementations must provide for consistent file chunk creation
  protected abstract sendChunk(
    buttonId: string,
    fileName: string,
    fileType: string,
    chunkData: ArrayBuffer,
    isLastChunk: boolean,
    fileSize: number,
    isFirstChunk: boolean,
    bytesUploaded: number,
    docType: string
  ): Promise<void>;

  // Common meeting termination
  public terminateMeeting(): void {
    const toServer = new ToAudioServer();
    toServer.setTerminateMeeting('User initiated');
    this.txAudio(toServer.serializeBinary());
  }

  // Common destroy method
  public async destroy(): Promise<void> {
    console.log('[BaseAudioManager] Destroy called');

    // Clean up resources
    await this.onDestroy();

    // Reset state
    this.lastKnownUpstreamStatusPrimary.value = UpstreamState.NO_CONNECTION;
    this.lastKnownUpstreamStatusObserver.value = UpstreamState.NO_CONNECTION;
    this.audioConnKey = null;
  }

  // Common transcript handling
  protected handleTranscript(transcript: AudioTranscript): void {
    console.log('[BaseAudioManager] Processing transcript', {
      source: transcript.getSource(),
      message: transcript.getMessage(),
      itemId: transcript.getItemId(),
      buttonsCount: transcript.getFileUploadButtonsList().length
    });

    // Get buttons from the transcript if available
    let buttons: MessageButton[] | undefined = undefined;
    let disableLastButtons = false;
    const buttonsList = transcript.getFileUploadButtonsList();

    if (buttonsList && buttonsList.length > 0) {
      // Check if this is a special "disable" message
      if (buttonsList.length === 1) {
        const specialButton = buttonsList[0];
        if (!specialButton.getId() && specialButton.getAction() === 'disable') {
          console.log('[BaseAudioManager] Received special button disable message');
          disableLastButtons = true;
          // Don't create buttons array - we'll just trigger disabling
        }
      }

      // If it's not a special disable message, create regular buttons
      if (!disableLastButtons) {
        buttons = buttonsList.filter((b: FileUploadButton) => {
          // Validate chat message buttons must have exactly 1 docType
          const availableDocTypes = b.getAvailableDocTypesList();
          if (availableDocTypes.length !== 1) {
            console.error('[BaseAudioManager] Invalid chat message button - must have exactly 1 docType:', {
              id: b.getId(),
              availableDocTypes: availableDocTypes
            });
            return false; // Filter out invalid buttons
          }
          return true;
        }).map((b: FileUploadButton) => {
          const button = {
            id: b.getId() || '',
            text: b.getText() || '',
            action: b.getAction() || '',
            disabled: false,
            backgroundColor: b.getBackgroundColor(),
            textColor: b.getTextColor(),
            borderColor: b.getBorderColor(),
            highlight: b.getHighlight(),
            availableDocTypes: b.getAvailableDocTypesList()
          };
          console.log('[BaseAudioManager] Created chat message button:', {
            id: button.id,
            text: button.text,
            action: button.action,
            availableDocTypes: button.availableDocTypes
          });
          return button;
        });
      }
    }

    // If this is a disable message, don't process it as a normal message
    if (disableLastButtons) {
      console.log('[BaseAudioManager] Detected special button disable message - not creating chat entry');
      // Now the frontend will auto-disable buttons in the most recent message
      return;
    }
    // Otherwise, process as normal message
    else {
      // Check if message is empty
      if (!transcript.getMessage().trim()) {
        console.warn('[BaseAudioManager] Empty transcript message detected:', {
          source: transcript.getSource(),
          itemId: transcript.getItemId()
        });
      }

      // Notify all speech message subscribers
      this.speechMessageCallbacks.forEach(callback => {
        callback(
          transcript.getSource(),
          transcript.getMessage(),
          transcript.getItemId(),
          buttons
        );
      });
    }
  }

  // Public method to handle incoming audio messages from multiplexed connection
  public handleIncomingMessage(msg: ToAudioClient): void {
    try {

      // Handle common message types
      switch (msg.getTypeCase()) {
        case ToAudioClient.TypeCase.AUDIO_TRANSCRIPT: {
          const transcript = msg.getAudioTranscript();
          if (transcript) {
            this.handleTranscript(transcript);
          }
          break;
        }

        case ToAudioClient.TypeCase.UPSTREAM_STATUSES: {
          const newUpstreamStatuses = msg.getUpstreamStatuses();
          if (newUpstreamStatuses) {
            this.lastKnownUpstreamStatusPrimary.value = newUpstreamStatuses.getPrimary();
            this.lastKnownUpstreamStatusObserver.value = newUpstreamStatuses.getObserver();
            console.log('[BaseAudioManager] Updated connection statuses:', {
              primary: this.lastKnownUpstreamStatusPrimary.value,
              observer: this.lastKnownUpstreamStatusObserver.value
            });
          }
          break;
        }

        case ToAudioClient.TypeCase.PARTICIPANT_LIST: {
          const participantList = msg.getParticipantList();
          if (participantList) {
            const participants = participantList.getParticipantsList();
            const ownerUuids = participantList.getOwnerUuidsList();
            const myUuid = participantList.getMyUuid();
            const update: ParticipantUpdate = {
              participants,
              ownerUuids: new Set(ownerUuids),
              currentUserUuid: myUuid
            };
            this.lastParticipantUpdate = update;
            this.participantUpdateCallbacks.forEach(callback => callback(update));
          }
          break;
        }

        case ToAudioClient.TypeCase.STATIC_FILE_UPLOAD_BUTTON: {
          const fileUploadButton = msg.getStaticFileUploadButton();
          if (fileUploadButton) {
            console.log('[BaseAudioManager] Received static file upload button:', {
              id: fileUploadButton.getId(),
              text: fileUploadButton.getText(),
              action: fileUploadButton.getAction(),
              visible: fileUploadButton.getVisible(),
              availableDocTypes: fileUploadButton.getAvailableDocTypesList()
            });
            console.log('[BaseAudioManager] Callbacks registered:', this.fileUploadButtonCallbacks.length);
            
            // Store the last static button for replay when callbacks are registered
            this.lastStaticFileUploadButton = fileUploadButton;
            
            this.fileUploadButtonCallbacks.forEach(callback => callback(fileUploadButton));
          }
          break;
        }

        case ToAudioClient.TypeCase.AUTODESK_OAUTH_URL: {
          const oauthUrl = msg.getAutodeskOauthUrl();
          if (oauthUrl) {
            console.log('[BaseAudioManager] Received Autodesk OAuth URL:', oauthUrl);
            // Open the OAuth URL in a new tab
            window.open(oauthUrl, '_blank');
          }
          break;
        }

        // Handle other cases in subclasses if needed
        default:
          this.handleSpecificMessage(msg);
          break;
      }
    } catch (error) {
      console.error('[BaseAudioManager] Error handling message:', error);
    }
  }

  // Abstract methods to be implemented by subclasses
  // Audio hardware specific 
  public abstract isRecording(): boolean;
  public abstract isPlayingBack(): boolean;
  public abstract toggleAudioMuting(): void;
  public abstract toggleMonologMode(): void;
  public abstract toggleObserverMode(): void;

  // File upload implementation varies
  public abstract uploadFile(
    buttonId: string,
    file: File,
    docType: string
  ): Promise<string>;

  // Hook methods for subclasses
  protected abstract onConnected(): Promise<void>;
  protected abstract onDestroy(): Promise<void>;
  protected abstract handleSpecificMessage(msg: ToAudioClient): void;
}